import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { AuthConfig } from '../modal/AuthConfig';
import { Location } from '@angular/common';
import { User } from '../modal/User';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { SalteAuth } from '@salte-auth/salte-auth';
import { Redirect } from '@salte-auth/redirect';
import { AuthService } from '@auth0/auth0-angular'; 
import { Subscription } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { DAO } from '../modal/DAO';

@Injectable({
  providedIn: 'root'
})
export  class AuthenticationService implements OnDestroy {

  public userSubject: BehaviorSubject<User | null>;
  /** The current user as an observable. This is a hot observable that starts with `null` so be sure to handle it */
  user$: Observable<User | null>;
  /** The current user as a normal object */
  get user(): User | null {
   
    if (window.localStorage.getItem("loginUserDetails")) {
      const user: User = JSON.parse(window.localStorage.getItem("loginUserDetails"));
      this.userSubject.next(user);
    }
    return this.userSubject.value;
  }
 
  /** true if there is a user authenticated */
  get isAuthenticated(): boolean {
    return this.user != null;
  }

  private accessTokenSubject: BehaviorSubject<string | null>;
  /** The access token for the current user as an observable. This is hot observable that starts with `null` */
  accessToken$: Observable<string | null>;
  /** The access token */
  get accessToken(): string | null {
    return this.accessTokenSubject.value;
  }

  private jwtSubject: BehaviorSubject<string | null>;
  /** The JWT for the current user as an observable. This is a hot observable that starts with `null` */
  jwt$: Observable<string | null>;
  /** The JWT  */
  get jwt(): string | null {
    return this.jwtSubject.value;
  }

  // private idTokenSubject: BehaviorSubject<string | null>;
  // idToken$: Observable<string | null>;
  // get idToken(): string | null {
  //   return this.idTokenSubject.value;
  // }
  private idTokenSubject: BehaviorSubject<string | null>;
  idToken$: Observable<string | null>;

  private authenticatingSubject: BehaviorSubject<boolean>;
  /** This observable emits true when the library is attempting to authenticate */
  authenticating$: Observable<boolean>;
  
  private salteAuth?: SalteAuth;
  /** The `AuthConfig` currently provided */
  currentConfig?: AuthConfig;

  constructor(private ngZone: NgZone, private authService: AuthService,public dao: DAO) {
    this.userSubject = new BehaviorSubject<User | null>(null);
    this.user$ = this.userSubject.asObservable();

    this.accessTokenSubject = new BehaviorSubject<string | null>(null);
    this.accessToken$ = this.accessTokenSubject.asObservable();

    this.jwtSubject = new BehaviorSubject<string | null>(null);
    this.jwt$ = this.jwtSubject.asObservable();

    this.idTokenSubject = new BehaviorSubject<string | null>(null);
    this.idToken$ = this.idTokenSubject.asObservable();

    this.authenticatingSubject = new BehaviorSubject<boolean>(false);
    this.authenticating$ = this.authenticatingSubject.asObservable();

    this.authService.idTokenClaims$.subscribe(claims => {
      if (claims) {
        const token = claims.__raw;
        this.getAccessTokenWithoutRetry();
        this.idTokenSubject.next(token);
      }
    });
  }

  public getAccessTokenWithoutRetry() {
    this.authService.getAccessTokenSilently()
      .pipe(
        catchError(err => {
          console.error('Error fetching access token:', err);
          return of(null); // Return observable of null if error occurs
        })
      )
      .subscribe(token => {
        if (token) {
          // Store the access token in the accessTokenSubject and dao object
          this.accessTokenSubject.next(token as string);
          
          // Ensure dao.loginUserDetail is initialized before assigning the token
          if (this.dao && this.dao.loginUserDetail) {
            this.dao.loginUserDetail.accessToken = token;
          } else {
            console.error('dao.loginUserDetail is not initialized');
          }
        } else {
          console.warn('Access token retrieval failed');
        }
      });
  }

  
  // private getAccessTokenWithRetry() {
  //   console.log('start getAccessTokenWithRetry');
  //   this.authService.getAccessTokenSilently()
  //     .pipe(
  //       timeout(20000), // Set timeout to 5 seconds or adjust as needed
  //       retry(3), // Retry up to 3 times
  //       catchError(err => {
  //         console.error('Error fetching access token:', err);
  //         return of(null);
  //       })
  //     )
  //     .subscribe(token => {
  //       if (token) {
  //         this.accessTokenSubject.next(token as string); // Add type assertion
  //         this.dao.loginUserDetail.accessToken = token;
  //         console.log(this.dao.loginUserDetail.accessToken ,'this.dao.loginUserDetail.accessToken')
  //       } else {
  //         console.warn('Token retrieval failed or timed out');
  //       }
  //     });
  //     console.log('end getAccessTokenWithRetry');
  // }
 
  /**
   * Set the AuthConfig to be used by the library and initialize SalteAuth. This must be called prior to login()
   */
  setAuthConfig(authConfig: AuthConfig) {
    if (this.currentConfig != null && this.currentConfig.configId !== authConfig.configId) {
      throw new Error(
        `Authetication: Cannot reinitialize AuthenticationService with AuthConfig with id ${authConfig.configId}`
      );
    }

    if (!this.salteAuth) {
      // save the config as the currentConfig for use in other parts of the library
      this.currentConfig = authConfig;

      // initialize SalteAuth
      //this.createNewSalteAuth(this.currentConfig);
    }
  }

  async login(): Promise<void> {
    try {
      await this.authService.loginWithRedirect();
    } catch (error) {
      console.error('Error logging in:', error);
    }
  }

  /**
   * This service is by default provided in the root provider, but if you provide it elsewhere it will perform cleanup
   * when the provider is destroy. This is usually only when provided with a component AFAIK
   */
  ngOnDestroy() {
    this.userSubject.complete();
    this.accessTokenSubject.complete();
    this.idTokenSubject.complete();
  }

   get idToken(): string | null {
    return this.idTokenSubject.value;
  }

  // private createNewSalteAuth(authConfig: AuthConfig) {
  //   this.salteAuth = new SalteAuth({
  //     providers: [
  //       new WSO2({
  //         url: authConfig.providerUrl,
  //         clientID: authConfig.clientId,
  //         responseType: 'id_token token',
  //         scope: 'openid',
  //         redirectUrl: authConfig.redirectUrl,
  //         storage: 'session',
  //       }),
  //     ],
  //     handlers: [
  //       new Redirect({
  //         default: true,
  //       }),
  //     ],
  //   });

  //   // try to set the observables for the tokens in case there are values already stored
  //   try {
  //     this.setTokenObs();
  //   } catch {}

  //   // register a callback that will be executed when the users has been logged in
  //   this.salteAuth.on('login', (error) => {
  //     if (error) {
  //       console.error(
  //         `Authentication: SalteAuth on login callback returned error: ${error.message}`,
  //         error
  //       );
  //     } else {
  //       this.ngZone.run(() => {
  //         this.setTokenObs();
  //         this.authenticatingSubject.next(false);
  //       });
  //     }
  //   });
  // }
    /**
     *  Update the token and id token based observables (userSubject/user$, accessTokenSubject/accessToken$, and jwtSubject/jwt$)
     */
  }

  

